import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThemeExportButton } from '../components/FundThemeFilter';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Landmark, Search, Plus, Loader2, ChevronRight, ChevronLeft, Sparkles, Layers, Pin, PinOff, Lock } from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import { CategoryBadge } from '../components/InfraDealsTable';
import { useScreenRun, ScreenResults } from '../components/InfraScreenResults';
import { useRowActions } from '../components/useRowActions';
import { IIImportPanel } from '../components/IIImportPanel';
import { WorkbookImportPanel } from '../components/WorkbookImportPanel';
import { useInfraScreenFilters, InfraScreenFilters } from '../components/InfraScreenFilters';
import { buildExploreBody } from '../components/DealFilterPanel';
import { useAuth } from '../contexts/AuthContext';
import { canManageInfraFunds } from '../lib/access';

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';
const STATUS = {
  ready: ['Profil fertig', 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10'],
  researching: ['Recherche läuft', 'text-amber-300 border-amber-500/40 bg-amber-500/10'],
  failed: ['Recherche fehlgeschlagen', 'text-red-400 border-red-500/40 bg-red-500/10'],
  new: ['Noch kein Profil', 'text-muted-foreground border-border bg-secondary'],
};
const PHASE = { queued: 'wartet (max. 3 parallel)', searching: 'Web-Recherche', crawling: 'Website lesen', profiling: 'Profil erstellen' };
export const parseFundLines = (text) => text.split('\n').map(l => l.trim()).filter(Boolean).map(l => {
  const [name, website] = l.split(/\s*[|;\t]\s*/);
  return { name: (name || '').trim(), website: (website || '').trim() };
}).filter(it => it.name);

function FundCard({ f, onOpen, onPin, canManage }) {
  const [label, cls] = STATUS[f.status] || STATUS.new;
  const researching = f.status === 'researching';
  return (
    <div role="button" tabIndex={0} onClick={() => onOpen(f.id)} onKeyDown={e => { if (e.key === 'Enter') onOpen(f.id); }} data-testid={`infra-fund-card-${f.id}`}
      className={`text-left bg-card border rounded-sm p-4 transition-colors group flex flex-col gap-2 cursor-pointer ${f.pinned ? 'border-amber-500/50 bg-amber-500/[0.04]' : 'border-border hover:border-amber-500/40'}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground truncate flex items-center gap-1.5">{f.pinned && <Pin className="w-3 h-3 text-amber-400 shrink-0" fill="currentColor" />}{f.name}</p>
          {f.website && <p className="text-[11px] text-muted-foreground truncate">{f.website.replace(/^https?:\/\/(www\.)?/, '')}</p>}
        </div>
        <span className="flex items-center gap-1 shrink-0">
          {canManage && (
          <button onClick={e => { e.stopPropagation(); onPin(f); }} data-testid={`infra-fund-pin-${f.id}`}
            title={f.pinned ? `Angepinnt${f.pinned_by_name ? ` von ${f.pinned_by_name}` : ''} — lösen` : 'Oben anpinnen'}
            className={`w-6 h-6 inline-flex items-center justify-center rounded-sm transition-colors ${f.pinned ? 'text-amber-400 hover:text-muted-foreground' : 'text-muted-foreground/35 hover:text-amber-300'}`}>
            {f.pinned ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}
          </button>
          )}
          <ChevronRight className="w-4 h-4 text-muted-foreground/30 group-hover:text-amber-300" />
        </span>
      </div>
      {f.summary && <p className="text-[11px] text-muted-foreground leading-snug line-clamp-3">{f.summary}</p>}
      {f.workbook && (
        <div className="space-y-1" data-testid={`infra-fund-wb-${f.id}`}>
          <p className="text-[11px] text-muted-foreground">
            <span className="text-[9px] font-bold text-emerald-300/80 border border-emerald-500/30 rounded-sm px-1 mr-1.5">XLS</span>
            {f.workbook.acquisitions} Käufe · {f.workbook.disposals} Verkäufe{f.workbook.vehicles?.length ? ` · ${f.workbook.vehicles.length} Vehikel` : ''}
          </p>
          {(f.workbook.sectors?.length > 0 || f.workbook.countries?.length > 0) && (
            <p className="text-[10px] text-muted-foreground/70 truncate">{[...f.workbook.sectors.slice(0, 2), ...f.workbook.countries.slice(0, 2)].join(' · ')}</p>
          )}
        </div>
      )}
      {f.ii && (f.ii.headquarters || f.ii.aum) && (
        <p className="text-[11px] text-muted-foreground" data-testid={`infra-fund-ii-${f.id}`}>
          <span className="text-[9px] font-bold text-sky-300/80 border border-sky-500/30 rounded-sm px-1 mr-1.5">II</span>
          {[f.ii.headquarters, f.ii.aum ? `AUM ${f.ii.aum}` : ''].filter(Boolean).join(' · ')}{f.ii.people ? ` · ${f.ii.people} Kontakte` : ''}
        </p>
      )}
      {researching && (
        <p className="text-[11px] text-amber-300/90 inline-flex items-center gap-1.5" data-testid={`infra-fund-phase-${f.id}`}>
          <Loader2 className="w-3 h-3 animate-spin" /> {PHASE[f.phase] || 'Recherche läuft'}{f.phase === 'searching' ? ` ${f.research_progress || 0}/3` : ''}
        </p>
      )}
      {f.status === 'failed' && f.error && <p className="text-[11px] text-red-400 line-clamp-2">{f.error}</p>}
      <div className="flex items-center gap-2 flex-wrap mt-auto pt-1">
        <span className={`text-[9px] font-medium border rounded-sm px-1.5 py-px ${cls}`}>{label}</span>
        <span className="text-[10px] font-mono text-amber-300/80" data-testid={`infra-fund-deals-${f.id}`}>{f.deals_count || 0} Deals</span>
        {f.deals_count > 0 && <span className="text-[10px] font-mono text-muted-foreground">{f.deals_researched || 0} recherchiert</span>}
        {f.researched_at && <span className="text-[10px] text-muted-foreground/50 ml-auto">Profil {fmtDate(f.researched_at)}</span>}
      </div>
    </div>
  );
}

const VIEWS = [['', 'Alle'], ['profiled', 'Mit Profil'], ['unprofiled', 'Ohne Profil'], ['pinned', 'Angepinnt']];

function AngleOverview({ rowActions }) {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState({});
  const screen = useScreenRun(data ? { run: data.run, rows: data.rows } : null);
  const load = useCallback(() => api.infraAllAngles().then(r => setData(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const sf = useInfraScreenFilters();
  if (!data) return null;
  const chosen = Object.keys(sel).filter(k => sel[k]);
  const startScreen = () => screen.start(api.infraScreenAngles(chosen, sf.body()));
  return (
    <div className="space-y-3" data-testid="angle-overview">
      <InfraScreenFilters f={sf} prefix="angles" accent="sky" appliesTo="das Angle-Screening (und alle Fonds-Screenings)" />
      <div className="bg-card border border-border rounded-sm">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between gap-3 flex-wrap">
          <div>
            <p className="text-[10px] uppercase tracking-widest font-bold text-sky-300/80 inline-flex items-center gap-1.5"><Layers className="w-3 h-3" /> Angle-Übersicht über alle Fonds</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Angrenzende Geschäftsmodelle aus {data.researched_deals} recherchierten Deals bei {data.funds} Fonds — je häufiger ein Angle, desto mehr Infra-Assets könnte eine Firma aus unserem Bestand bedienen. Ohne Stage-Filter werden nur Firmen in Succession Possible + OLTA TAM gescreent.
            </p>
          </div>
          <button onClick={startScreen} disabled={!data.angles.length || screen.run?.status === 'running'} data-testid="angles-screen-btn"
            className="inline-flex items-center gap-1.5 text-xs font-medium rounded-sm px-3 h-8 bg-sky-700 hover:bg-sky-600 text-white disabled:opacity-50 shrink-0">
            {screen.run?.status === 'running' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            {chosen.length ? `${chosen.length} ausgewählte Angles screenen` : 'Top-Angles screenen (max. 25)'}
          </button>
        </div>
        {data.angles.length === 0 ? (
          <p className="px-4 py-6 text-xs text-muted-foreground text-center" data-testid="angles-empty">Noch keine recherchierten Deals — in einem Fonds Deals einspielen und „Deals recherchieren“ starten.</p>
        ) : (
          <div className="divide-y divide-border/40 max-h-[520px] overflow-y-auto">
            {data.angles.map((a, i) => (
              <label key={i} className="flex items-start gap-3 px-4 py-2 hover:bg-secondary/30 cursor-pointer" data-testid={`angle-row-${i}`}>
                <Checkbox checked={!!sel[a.label]} onCheckedChange={v => setSel(prev => ({ ...prev, [a.label]: !!v }))} className="mt-0.5" />
                <span className="text-sm font-mono font-bold text-sky-300 w-8 shrink-0">{a.count}×</span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap"><span className="text-xs text-foreground font-medium">{a.label}</span><CategoryBadge category={a.category} /></div>
                  {a.descriptions?.[0] && <p className="text-[11px] text-muted-foreground leading-snug">{a.descriptions[0]}</p>}
                  <p className="text-[10px] text-muted-foreground/70 truncate">
                    {a.deals.slice(0, 6).map(d => `${d.target} (${d.fund_name})`).join(' · ')}{a.deals.length > 6 ? ` · +${a.deals.length - 6}` : ''}
                  </p>
                </div>
              </label>
            ))}
          </div>
        )}
      </div>
      <ScreenResults run={screen.run} rows={screen.rows} label={`für ${screen.run?.fund_name || 'die gewählten Angles'}`} prefix="anglescreen"
        onRescreen={startScreen} onReset={screen.reset} rowProps={rowActions.rowProps} decorate={rowActions.decorate} fundName="Angle-Screening" currentFilters={buildExploreBody(sf.filters)} />
    </div>
  );
}

const SORTS = [['created', 'Neueste'], ['deals', 'Meiste Deals'], ['name', 'Name']];

export default function InfraFundsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = canManageInfraFunds(user);
  const [funds, setFunds] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, pages: 1, all_funds: 0, all_deals: 0, researching: 0 });
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState('created');
  const [view, setView] = useState(() => { try { return sessionStorage.getItem('infra-fund-view') || ''; } catch { return ''; } });
  const [name, setName] = useState('');
  const [website, setWebsite] = useState('');
  const [research, setResearch] = useState(true);
  const [creating, setCreating] = useState(false);
  const rowActions = useRowActions('/infra-funds');

  const load = useCallback(() => api.fundList({ q, page, sort, limit: 60, status: view }).then(r => {
    setFunds(r.data.funds || []);
    setMeta({ total: r.data.total || 0, page: r.data.page || 1, pages: r.data.pages || 1, all_funds: r.data.all_funds || 0, all_deals: r.data.all_deals || 0,
      researching: r.data.researching || 0, profiled: r.data.profiled || 0, pinned: r.data.pinned || 0 });
  }).catch(() => {}), [q, page, sort, view]);
  useEffect(() => { const t = setTimeout(load, q ? 300 : 0); return () => clearTimeout(t); }, [load, q]);
  useEffect(() => { setPage(1); }, [q, sort, view]);
  useEffect(() => { try { sessionStorage.setItem('infra-fund-view', view); } catch { /* ignore */ } }, [view]);
  const togglePin = async (f) => {
    const next = !f.pinned;
    setFunds(prev => prev.map(x => (x.id === f.id ? { ...x, pinned: next } : x)));
    try { await api.fundPin(f.id, next); toast.success(next ? `„${f.name}“ oben angepinnt` : `„${f.name}“ gelöst`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Anpinnen fehlgeschlagen'); load(); }
  };
  const researchingCount = meta.researching;
  useEffect(() => {
    if (!researchingCount) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [researchingCount, load]);

  const items = parseFundLines(name);
  const multi = items.length > 1;

  const create = async () => {
    if (!items.length || items[0].name.length < 3) { toast.error('Bitte den Namen des Fonds eingeben'); return; }
    setCreating(true);
    try {
      if (!multi) {
        const r = await api.fundCreate(items[0].name, items[0].website || website.trim(), {}, research);
        toast.success(research ? `„${r.data.name}“ angelegt — Recherche läuft` : `„${r.data.name}“ angelegt`);
        navigate(`/infra-funds/${r.data.id}`);
        return;
      }
      const r = await api.fundCreateBulk(items, research);
      const { created, skipped } = r.data;
      if (created.length) {
        toast.success(`${created.length} Fonds angelegt${research ? ` — Recherche läuft (max. ${r.data.parallel} parallel, Rest wartet)` : ''}`);
      }
      if (skipped.length) {
        toast.warning(`${skipped.length} übersprungen: ${skipped.map(s => `${s.name} (${s.reason})`).join(' · ')}`, { duration: 9000 });
      }
      setName(''); setWebsite('');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Anlegen fehlgeschlagen'); }
    finally { setCreating(false); }
  };

  return (
    <div className="space-y-4" data-testid="infra-funds-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-heading text-2xl text-foreground tracking-tight flex items-center gap-2">
            <Landmark className="w-6 h-6 text-amber-400" /> Infra-Fonds
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Eigene Datenbank der Infrastruktur-Investoren: Profil, Strategie und alle Deals pro Fonds — die KI leitet daraus angrenzende Geschäftsmodelle ab und findet passende Firmen in unserem Bestand.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <ThemeExportButton label={`Themen-Excel (${(meta.profiled || 0).toLocaleString('de-DE')} Fonds, 1 Blatt je Fonds)`} testid="infra-funds-theme-export"
            className="border border-border rounded-sm px-2.5 h-8 text-[11px]" />
          <span className="text-[11px] text-muted-foreground font-mono" data-testid="infra-funds-count">
            {meta.all_funds.toLocaleString('de-DE')} Fonds · {meta.all_deals.toLocaleString('de-DE')} Deals{researchingCount ? ` · ${researchingCount} in Recherche` : ''}
          </span>
        </div>
      </div>

      <div className="bg-card border border-border rounded-sm p-4 space-y-3">
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Fonds suchen — Name, Sektor, Land, Fonds-Vehikel, Website…" data-testid="infra-fund-search"
              className="w-full bg-secondary border border-border rounded-sm pl-9 pr-3 h-10 text-sm text-foreground focus:outline-none focus:border-amber-500/60" />
          </div>
          <div className="flex items-center gap-1 shrink-0" data-testid="infra-fund-sort">
            {SORTS.map(([k, label]) => (
              <button key={k} onClick={() => setSort(k)} data-testid={`infra-fund-sort-${k}`}
                className={`text-[11px] rounded-sm px-2.5 h-10 border ${sort === k ? 'border-amber-500/60 text-amber-300 bg-amber-500/10' : 'border-border text-muted-foreground hover:text-foreground'}`}>{label}</button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="infra-fund-view">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground/70 mr-1">Zeige</span>
          {VIEWS.map(([k, label]) => {
            const n = k === 'profiled' ? meta.profiled : k === 'pinned' ? meta.pinned : k === 'unprofiled' ? Math.max(0, meta.all_funds - (meta.profiled || 0)) : meta.all_funds;
            return (
              <button key={k || 'all'} onClick={() => setView(k)} data-testid={`infra-fund-view-${k || 'all'}`}
                className={`inline-flex items-center gap-1.5 text-[11px] rounded-full px-2.5 h-7 border transition-colors ${view === k ? 'border-amber-500/60 text-amber-300 bg-amber-500/10' : 'border-border text-muted-foreground hover:text-foreground'}`}>
                {k === 'pinned' && <Pin className="w-3 h-3" />}{label}
                {n != null && <span className="font-mono text-[10px] opacity-70">{n.toLocaleString('de-DE')}</span>}
              </button>
            );
          })}
        </div>
        <div className="flex flex-col sm:flex-row gap-2 border-t border-border/40 pt-3 sm:items-start">
          {!canManage ? (
            <p className="text-[11px] text-muted-foreground inline-flex items-start gap-1.5" data-testid="infra-fund-screen-only-hint">
              <Lock className="w-3 h-3 mt-0.5 shrink-0 text-sky-300" />
              <span><span className="text-sky-300 font-medium">Nur Deals suchen:</span> Sie können jeden Fonds öffnen und unsere Datenbank gegen sein Profil, seine Themen, Angles und Portfolio-Deals screenen.
              Neue Fonds anlegen, Profile recherchieren oder Deals importieren/recherchieren (kostenpflichtig) übernimmt der Admin.</span>
            </p>
          ) : (
          <>
          <div className="flex-1 min-w-0">
            <textarea value={name} onChange={e => setName(e.target.value)} rows={Math.min(Math.max(name.split('\n').length, 1), 8)}
              placeholder="Neuen Fonds anlegen — z. B. Arcus Infrastructure Partners (mehrere: eine Zeile pro Fonds)"
              data-testid="infra-fund-name-input"
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey && (e.metaKey || e.ctrlKey || !name.includes('\n'))) { e.preventDefault(); create(); } }}
              className="w-full bg-secondary border border-border rounded-sm px-3 py-2 text-sm text-foreground focus:outline-none focus:border-amber-500/60 resize-none leading-5" />
            <p className="text-[10px] text-muted-foreground/70 mt-1" data-testid="infra-fund-input-hint">
              {multi ? `${items.length} Fonds erkannt — werden gleichzeitig angelegt und recherchiert (max. 3 parallel, Rest wartet). Strg/⌘ + Enter zum Anlegen.`
                : 'Enter = anlegen · Shift+Enter = weitere Zeile für mehrere Fonds auf einmal'}
            </p>
          </div>
          {!multi && (
            <input value={website} onChange={e => setWebsite(e.target.value)} placeholder="Website (optional)" data-testid="infra-fund-website-input"
              className="sm:w-56 bg-secondary border border-border rounded-sm px-3 h-9 text-sm text-foreground focus:outline-none focus:border-amber-500/60" />
          )}
          <label className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground cursor-pointer shrink-0 h-9">
            <Checkbox checked={research} onCheckedChange={v => setResearch(!!v)} data-testid="infra-fund-research-toggle" /> sofort recherchieren
          </label>
          <button onClick={create} disabled={creating} data-testid="infra-fund-create-btn"
            className="inline-flex items-center justify-center gap-1.5 text-xs font-medium rounded-sm px-4 h-9 bg-amber-600 hover:bg-amber-500 text-white disabled:opacity-50 shrink-0">
            {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} {multi ? `${items.length} Fonds anlegen` : 'Anlegen'}
          </button>
          </>
          )}
        </div>
      </div>

      {canManage && <WorkbookImportPanel onChanged={load} />}
      {canManage && <IIImportPanel onSaved={load} />}

      {funds.length === 0 ? (
        <p className="text-center py-10 text-sm text-muted-foreground" data-testid="infra-funds-empty">
          {view === 'pinned' && !q ? 'Noch nichts angepinnt — auf einer Karte das Pin-Symbol klicken, dann steht der Fonds immer oben.'
            : view === 'profiled' && !q ? 'Noch kein Fonds mit Profil — „Profil recherchieren“ auf einer Fonds-Seite oder „sofort recherchieren“ beim Anlegen.'
            : meta.all_funds ? 'Kein Fonds passt zur Suche.' : 'Noch kein Fonds angelegt — oben Namen eingeben, Workbooks importieren oder im KI Deal Screening (Schritt 4) recherchieren.'}
        </p>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="infra-fund-grid">
            {funds.map(f => <FundCard key={f.id} f={f} onOpen={(id) => navigate(`/infra-funds/${id}`)} onPin={togglePin} canManage={canManage} />)}
          </div>
          <div className="flex items-center justify-between gap-3 flex-wrap text-[11px] text-muted-foreground" data-testid="infra-fund-pager">
            <span data-testid="infra-fund-pager-info">{(q || view) ? `${meta.total.toLocaleString('de-DE')} Treffer · ` : ''}Seite {meta.page} von {meta.pages}</span>
            <div className="flex items-center gap-1">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={meta.page <= 1} data-testid="infra-fund-prev"
                className="inline-flex items-center gap-1 rounded-sm px-2.5 h-8 border border-border hover:text-foreground disabled:opacity-40"><ChevronLeft className="w-3.5 h-3.5" /> Zurück</button>
              <button onClick={() => setPage(p => Math.min(meta.pages, p + 1))} disabled={meta.page >= meta.pages} data-testid="infra-fund-next"
                className="inline-flex items-center gap-1 rounded-sm px-2.5 h-8 border border-border hover:text-foreground disabled:opacity-40">Weiter <ChevronRight className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        </>
      )}

      <AngleOverview rowActions={rowActions} />
      {rowActions.passDialog}
    </div>
  );
}
